import classnames from 'classnames/bind'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { Spinner } from '@unlikelystudio/react-abstract-components'

import OrderProducts from '~/components/Account/Logged/Orders/OrderDetails/OrderProducts'
import { Order } from '~/components/Account/Logged/Orders/types'

import { useStyle } from '~/providers/StyleProvider'

import { ACCOUNT, ORDER } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

type OrderDetailsProps = Order & {
  className?: string
  isLoading: boolean
  paymentDetails: {
    formattedGateway: string
    accountNumber: string
  }
}

function OrderDetails({
  className,
  products,
  date,
  shippingPrice,
  shippingAddress,
  paymentDetails,
  isLoading,
  total,
  status,
}: OrderDetailsProps) {
  const t = useTranslate()

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const renderPartial = ({ key, value }: { key: string; value: string }) => {
    return (
      <div className={css.partial}>
        <span className={cx(css.title, textStyle)}>{t(key)}</span>
        {value && <p>{value}</p>}
      </div>
    )
  }

  return (
    <div className={cx(css.OrderDetails, className)}>
      <OrderProducts className={css.products} products={products} />
      <div className={cx(textStyle, css.instructions)}>
        <div>
          {renderPartial({
            key: ACCOUNT.ORDER_SHIPPING_PRICE,
            value: shippingPrice,
          })}
          {renderPartial({
            key: ACCOUNT.TOTAL,
            value: total,
          })}
        </div>

        <div className={cx(css.partialWrapper, css.billing)}>
          <div className={css.partial}>
            <span className={cx(css.title, textStyle)}>
              {t(ACCOUNT.ORDER_BILLING_DETAILS)}
            </span>
            {isLoading ? (
              <Spinner className={css.spinner} />
            ) : (
              <div className={css.content}>
                {paymentDetails?.formattedGateway && (
                  <div className={cx(css.contentLine)}>
                    {paymentDetails.formattedGateway}
                  </div>
                )}
                {paymentDetails?.accountNumber && (
                  <div className={cx(css.contentLine)}>
                    {paymentDetails.accountNumber}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={css.partialWrapper}>
          {renderPartial({
            key: ACCOUNT.CUSTOMER,
            value: shippingAddress?.name,
          })}
        </div>

        <div className={css.partialWrapper}>
          {renderPartial({ key: ACCOUNT.DATE, value: date })}
          {renderPartial({
            key: ACCOUNT.ORDER_STATUS,
            value:
              ORDER?.[`STATUS_${status?.toUpperCase()}`] ??
              ORDER.STATUS_UNKNOWN,
          })}
        </div>
      </div>
    </div>
  )
}

export default OrderDetails
